/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { dFix, getSettings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { InventoryResponse, ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { RequestQuery } from '@/data/types/RequestQuery';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { error as logError } from '@/data/utils/loggerUtil';
import { transactionsInventoryAvailability } from 'integration/generated/transactions';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import { useMemo } from 'react';
import useSWR, { unstable_serialize as unstableSerialize } from 'swr';

const ONLINE_STORE_KEY = 'Online';
const AVAILABLE_KEY = 'Available';
const DATA_KEY_INVENTORY = 'inventory';
const DATA_KEY_INVENTORY_AVAILABILITY_ORDER_ID = 'inventoryAvailability';

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		storeId: string,
		partNumber: string, // CSV
		query: RequestQuery = {},
		params: RequestParams
	): Promise<InventoryResponse | undefined> => {
		try {
			const res = (await transactionsInventoryAvailability(
				pub
			).inventoryAvailabilityGetInventoryAvailabilityByPartNumber(
				storeId,
				partNumber,
				query,
				params
			)) as InventoryResponse;

			return res;
		} catch (error) {
			if (pub) {
				throw error;
			}

			logError(context?.req, '_Inventory: fetcher: error: %o', error);
			return undefined;
		}
	};

const dataMap = (data: InventoryResponse): ProductAvailabilityData[] => {
	// open-api spec typing isn't accurate -- using custom type here
	const all = data.InventoryAvailability ?? [];

	return all.map(
		(pn) =>
			({
				...(pn.onlineStoreId && {
					...pn,
					storeId: pn.onlineStoreId,
					storeName: ONLINE_STORE_KEY,
					status: pn.inventoryStatus === AVAILABLE_KEY,
				}),
				...(pn.physicalStoreId && {
					...pn,
					physicalStoreId: pn.physicalStoreId,
					storeName: pn.physicalStoreName,
					physicalStoreStatus: pn.inventoryStatus === AVAILABLE_KEY,
				}),
			} as ProductAvailabilityData)
	);
};

export const getInventoryRecord = (
	inventories: ProductAvailabilityData[],
	partNumber: string,
	storeName = ONLINE_STORE_KEY
) =>
	inventories?.find(
		({ partNumber: _pn, storeName: _store }) => _pn === partNumber && _store === storeName
	) as ProductAvailabilityData;

export const hasInStock = (availability: ProductAvailabilityData | undefined, quantity?: number) =>
	availability?.inventoryStatus === AVAILABLE_KEY &&
	(quantity === undefined || quantity <= dFix(availability.availableQuantity ?? 0, 0));

export const getInventory = async (
	cache: Cache,
	partNumber: string,
	context: GetServerSidePropsContext
): Promise<
	{
		key: string;
		value: InventoryResponse;
	}[]
> => {
	const settings = await getSettings(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);
	const props = { storeId, partNumber, langId };
	const key = unstableSerialize([shrink(props), DATA_KEY_INVENTORY]);
	const params = constructRequestParamsWithPreviewToken({ context });
	const value = cache.get(key) || fetcher(false, context)(storeId, partNumber, {}, params);
	cache.set(key, value);
	return await value;
};

/**
 *
 * @param ids comma-separated specification of partNumbers
 */
export const useInventory = (ids = '', physicalStoreName = '') => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { data, error, isLoading } = useSWR(
		storeId && ids
			? [
					shrink({
						storeId,
						partNumber: ids as string,
						langId,
						...(physicalStoreName && { query: { physicalStoreName } }),
					}),
					DATA_KEY_INVENTORY,
			  ]
			: null,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(props);
			const { storeId, partNumber, query } = expanded;
			return fetcher(true)(storeId, partNumber, query, params);
		},
		// defect HC-34836
		{ revalidateIfStale: true, dedupingInterval: 30000 }
	);
	const availability = useMemo(() => (data ? dataMap(data) : data), [data]);

	return {
		availability,
		hasInventory: !!availability?.find((iv) => iv.status || iv.physicalStoreStatus),
		loading: !error && !data && isLoading,
		error,
	};
};

export const useInventoryArray = (ids: string[] = [], physicalStoreName = '') => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const { data, error, isLoading } = useSWR(
		storeId && ids.length > 0
			? [
					shrink({
						storeId,
						partNumbers: ids as string[],
						langId,
						...(physicalStoreName && { query: { physicalStoreName } }),
					}),
					DATA_KEY_INVENTORY,
			  ]
			: null,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(props);
			const { storeId, partNumbers, query } = expanded;
			const fetchDataPromises = partNumbers.map((partNumber: string) =>
				fetcher(true)(storeId, partNumber, query, params)
			);
			const responses = await Promise.all(fetchDataPromises);
			return responses;
		},
		// defect HC-34836
		{ revalidateIfStale: true, dedupingInterval: 30000 }
	);

	const availability = useMemo(() => {
		if (data) {
			return data.flatMap((response) => dataMap(response));
		}
		return data;
	}, [data]);

	return {
		availability,
		hasInventory: !!availability?.find((iv) => iv.status || iv.physicalStoreStatus),
		loading: !error && !data && isLoading,
		error,
	};
};

const inventoryByOrderIdFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (storeId: string, orderId: string, query: RequestQuery = {}, params: RequestParams) => {
		try {
			const res = await transactionsInventoryAvailability(
				pub
			).inventoryAvailabilityGetAvailabilityByOrderId(storeId, orderId, query, params);
			return res;
		} catch (error) {
			if (pub) {
				throw error;
			}

			logError(context?.req, '_Inventory: fetcherInventoryByOrderId: error: %o', error);
			return undefined;
		}
	};

export const useInventoryByOrderId = (orderId?: string) => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const { data, error, isLoading } = useSWR(
		storeId && orderId
			? [
					shrink({
						storeId,
						orderId,
						langId,
					}),
					DATA_KEY_INVENTORY_AVAILABILITY_ORDER_ID,
			  ]
			: null,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(props);
			const { storeId, orderId, query } = expanded;
			return inventoryByOrderIdFetcher(true)(storeId, orderId, query, params);
		},
		{ revalidateIfStale: true }
	);

	return {
		availability: data?.response ?? [],
		loading: !error && !data && isLoading,
		error,
	};
};
